import { Injectable } from '@angular/core';
import { Person } from '../core-data-model/Person.model';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { PersonApi } from './PersonApi.connect';
import { SearchApi } from './SearchApi.connect';

export enum SelfIdentityMatchState {
    MatchExists = 1,
    MatchFound,
    MatchNotFound
}

/**
 * For matching the User to the requisite SurveyUser (Person)
 * @deprecated For Legacy resolution of User to SurveyUser association. See {@link IdentityMatchDataService} for PI2 resolution.
 */
@Injectable({ providedIn: 'root' })
export class SelfIdentityDataService {
    matchState: SelfIdentityMatchState;
    potentialMatch: Person;
    self: Person;
    _checkIdentityPromise: Promise<void> = null;

    constructor(
        private searchApi: SearchApi,
        private personApi: PersonApi,
        private initData: InitDataService
    ) {
        this.matchState = this.initData.user.surveyUserId
            ? SelfIdentityMatchState.MatchExists
            : SelfIdentityMatchState.MatchNotFound;
    }

    /**
     * Await this promise to resolve any ambiguity over the logged in user's PERSON/SURVEYUSER identity.
     *
     * @deprecated This is LEGACY identity resolution, see MatchIdentity method {@link checkIdentityPI2}
     */
    get checkIdentity(): Promise<void> {
        if (!this._checkIdentityPromise) {
            this._checkIdentityPromise = this.selfPromise();
        }
        return this._checkIdentityPromise;
    }

    async setSelf(person: Person): Promise<any> {
        if (this.self && this.self.id !== person.id) {
            await this.unsetSelf();
        }
        if (person) {
            this.personApi.setAssociation(
                person.id,
                this.initData.user.userGuid
            );
            this.initData.user.surveyUserId = person.id;
            this.self = person;
            this.matchState = SelfIdentityMatchState.MatchExists;
        }
    }

    async unsetSelf() {
        if (!this.self) {
            return;
        }
        await this.personApi.unsetAssociation(this.self.id);
        this.initData.user.surveyUserId = null;
        this.self = null;
        this.matchState = SelfIdentityMatchState.MatchNotFound;
    }

    async findMe(personId?: string): Promise<Person> {
        personId ??= this.initData.user.surveyUserId;
        const [person] = await this.personApi.getPersonDetails([personId]);
        this.self = person;
        return person;
    }

    /**
     * If an identity match exists, get their person details. If not, search for a match to this user's identity.
     *
     * @deprecated the business rules for identity resolution has changed, see equivalent MatchIdentity method
     */
    private async selfPromise(): Promise<void> {
        if (this.matchState === SelfIdentityMatchState.MatchExists) {
            const me = await this.findMe();
            this.self = me;
        } else {
            const match = await this.searchForMyMatch();
            this.potentialMatch = match;
            if (match) {
                this.matchState = SelfIdentityMatchState.MatchFound;
            } else {
                this.matchState = SelfIdentityMatchState.MatchNotFound;
            }
        }
    }

    /**
     * Searches for a PERSON/SURVEYUSER match by first name, last name, and email.
     *
     * @deprecated This method calls peopleAndJobs instead of pi-client-global-search.
     * It also includes name fields which are longer used for matching.
     */
    private searchForMyMatch(): Promise<Person> {
        return this.searchApi.searchForMatch(
            this.initData.user.firstName,
            this.initData.user.lastName,
            this.initData.user.email
        );
    }
}
